import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { useLang } from "@/i18n";
import { ArrowLeft, ArrowRight, Loader2, Send, Clock, Check } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORE_KEY = "mc_wizard";

const T = {
  nl: {
    title: "Schoonmaker aanvragen",
    intro: "Uw aanvraag duurt ongeveer 2 minuten. Alleen de belangrijkste gegevens zijn verplicht.",
    steps: ["Contact", "Adres & woning", "Planning", "Extra (optioneel)", "Overzicht"],
    step: "Stap", of: "van",
    name: "Volledige naam", email: "E-mailadres", phone: "Telefoonnummer",
    street: "Straat + huisnummer", postcode: "Postcode", city: "Plaats",
    homeType: "Type woning",
    homeTypes: ["Appartement", "Tussenwoning", "Hoekwoning", "Vrijstaande woning", "Anders"],
    frequency: "Gewenste frequentie",
    frequencies: ["Eenmalig", "Wekelijks", "Om de week", "Maandelijks"],
    startDate: "Gewenste startdatum",
    hours: "Aantal uren per afspraak",
    hoursOpts: ["2 uur", "3 uur", "4 uur", "Anders"],
    bedrooms: "Aantal slaapkamers", bathrooms: "Aantal badkamers",
    days: "Voorkeursdagen",
    daysOpts: ["Ma", "Di", "Wo", "Do", "Vr", "Za"],
    pets: "Huisdieren aanwezig?",
    petsOpts: ["Ja", "Nee"],
    fixed: "Voorkeur voor vaste schoonmaker?",
    fixedOpts: ["Ja", "Nee", "Geen voorkeur"],
    notes: "Opmerkingen of bijzonderheden",
    notesPh: "Bijv. allergie, specifieke wensen...",
    optionalHint: "Deze stap is optioneel. Vul in wat u wilt of sla over.",
    reviewTitle: "Controleer uw aanvraag",
    next: "Volgende", prev: "Vorige", skip: "Overslaan", submit: "Aanvraag versturen", sending: "Versturen...",
    required: "Vul de verplichte velden in.",
    success: "Bedankt! Uw aanvraag is verzonden. We nemen snel contact op.",
    error: "Er ging iets mis. Probeer opnieuw of app ons.",
    optional: "optioneel", none: "—",
  },
  en: {
    title: "Request a cleaner",
    intro: "Your request takes about 2 minutes. Only the essentials are required.",
    steps: ["Contact", "Address & home", "Schedule", "Extra (optional)", "Review"],
    step: "Step", of: "of",
    name: "Full name", email: "Email address", phone: "Phone number",
    street: "Street + number", postcode: "Postcode", city: "City",
    homeType: "Type of home",
    homeTypes: ["Apartment", "Terraced house", "Corner house", "Detached house", "Other"],
    frequency: "Desired frequency",
    frequencies: ["One-off", "Weekly", "Every two weeks", "Monthly"],
    startDate: "Preferred start date",
    hours: "Hours per visit",
    hoursOpts: ["2 hours", "3 hours", "4 hours", "Other"],
    bedrooms: "Bedrooms", bathrooms: "Bathrooms",
    days: "Preferred days",
    daysOpts: ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat"],
    pets: "Any pets present?",
    petsOpts: ["Yes", "No"],
    fixed: "Prefer a regular cleaner?",
    fixedOpts: ["Yes", "No", "No preference"],
    notes: "Notes or remarks",
    notesPh: "E.g. allergy, specific wishes...",
    optionalHint: "This step is optional. Fill in what you like or skip it.",
    reviewTitle: "Review your request",
    next: "Next", prev: "Back", skip: "Skip", submit: "Send request", sending: "Sending...",
    required: "Please fill in the required fields.",
    success: "Thank you! Your request has been sent. We'll be in touch shortly.",
    error: "Something went wrong. Please try again or message us.",
    optional: "optional", none: "—",
  },
};

const empty = {
  name: "", email: "", phone: "",
  street: "", postcode: "", city: "", homeType: "",
  frequency: "", startDate: "", hours: "",
  bedrooms: "", bathrooms: "", preferredDays: [], pets: "", fixedCleaner: "", notes: "",
};

const fieldCls = "rounded-xl border-slate-200 focus-visible:ring-blue-600 focus-visible:border-blue-600";

const Chip = ({ active, onClick, children, testid }) => (
  <button
    type="button"
    data-testid={testid}
    onClick={onClick}
    className={`rounded-full border-2 px-4 py-2 text-sm font-medium transition-all ${
      active ? "border-blue-600 bg-blue-600 text-white" : "border-slate-200 bg-white text-slate-700 hover:border-blue-300"
    }`}
  >
    {children}
  </button>
);

const TOTAL = 5;

const Field = ({ label, optional, optionalText, children }) => (
  <div className="space-y-1.5">
    <Label className="text-sm font-medium text-slate-700">
      {label} {optional && <span className="text-xs font-normal text-blue-500">({optionalText})</span>}
    </Label>
    {children}
  </div>
);

const SummaryRow = ({ label, value }) => (
  <div className="flex justify-between gap-4 border-b border-slate-100 py-2 text-sm">
    <span className="text-slate-500">{label}</span>
    <span className="text-right font-medium text-slate-800">{value || "—"}</span>
  </div>
);

export const RequestWizard = ({ open, onOpenChange }) => {
  const { lang } = useLang();
  const L = T[lang];
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState(() => {
    try {
      const saved = localStorage.getItem(STORE_KEY);
      return saved ? { ...empty, ...JSON.parse(saved) } : empty;
    } catch {
      return empty;
    }
  });

  useEffect(() => {
    localStorage.setItem(STORE_KEY, JSON.stringify(form));
  }, [form]);

  const set = (k, v) => {
    setError("");
    setForm((f) => ({ ...f, [k]: v }));
  };
  const setInput = (k) => (e) => set(k, e.target.value);
  const toggleArr = (k, v) => {
    setError("");
    setForm((f) => ({ ...f, [k]: f[k].includes(v) ? f[k].filter((x) => x !== v) : [...f[k], v] }));
  };

  const validStep = () => {
    if (step === 0) return form.name && form.email && form.phone;
    if (step === 1) return form.street && form.postcode && form.city && form.homeType;
    if (step === 2) return form.frequency && form.startDate;
    return true;
  };

  const next = () => {
    if (!validStep()) return setError(L.required);
    setError("");
    setStep((s) => Math.min(s + 1, TOTAL - 1));
  };
  const prev = () => {
    setError("");
    setStep((s) => Math.max(s - 1, 0));
  };

  const submit = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/leads/customer`, form);
      toast.success(L.success);
      localStorage.removeItem(STORE_KEY);
      setForm(empty);
      setStep(0);
      onOpenChange(false);
    } catch {
      toast.error(L.error);
    } finally {
      setLoading(false);
    }
  };

  const progress = Math.round(((step + 1) / TOTAL) * 100);
  const isOptionalStep = step === 3;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="request-wizard" className="max-h-[92vh] gap-0 overflow-hidden rounded-3xl p-0 sm:max-w-xl">
        <div className="border-b border-slate-100 px-6 pt-6 pb-4">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">{L.title}</DialogTitle>
            <DialogDescription className="flex items-center gap-1.5 text-slate-500">
              <Clock className="h-4 w-4 text-blue-500" /> {L.intro}
            </DialogDescription>
          </DialogHeader>
          <div className="mt-4">
            <div className="mb-2 flex items-center justify-between text-xs font-semibold text-slate-500">
              <span>{L.step} {step + 1} {L.of} {TOTAL} · {L.steps[step]}</span>
              <span>{progress}%</span>
            </div>
            <div className="h-2 w-full overflow-hidden rounded-full bg-slate-100">
              <motion.div
                className="h-full rounded-full bg-blue-600"
                animate={{ width: `${progress}%` }}
                transition={{ duration: 0.3 }}
                data-testid="wizard-progress"
              />
            </div>
          </div>
        </div>

        <div className="max-h-[54vh] overflow-y-auto px-6 py-6">
          <AnimatePresence mode="wait">
            <motion.div
              key={step}
              initial={{ opacity: 0, x: 24 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -24 }}
              transition={{ duration: 0.25 }}
              className="space-y-5"
            >
              {step === 0 && (
                <>
                  <Field optionalText={L.optional} label={L.name}><Input data-testid="wiz-name" value={form.name} onChange={setInput("name")} className={fieldCls} /></Field>
                  <Field optionalText={L.optional} label={L.email}><Input data-testid="wiz-email" type="email" value={form.email} onChange={setInput("email")} className={fieldCls} /></Field>
                  <Field optionalText={L.optional} label={L.phone}><Input data-testid="wiz-phone" value={form.phone} onChange={setInput("phone")} className={fieldCls} /></Field>
                </>
              )}

              {step === 1 && (
                <>
                  <Field optionalText={L.optional} label={L.street}><Input data-testid="wiz-street" value={form.street} onChange={setInput("street")} className={fieldCls} /></Field>
                  <div className="grid grid-cols-2 gap-4">
                    <Field optionalText={L.optional} label={L.postcode}><Input data-testid="wiz-postcode" value={form.postcode} onChange={setInput("postcode")} className={fieldCls} /></Field>
                    <Field optionalText={L.optional} label={L.city}><Input data-testid="wiz-city" value={form.city} onChange={setInput("city")} className={fieldCls} /></Field>
                  </div>
                  <Field optionalText={L.optional} label={L.homeType}>
                    <div className="flex flex-wrap gap-2">
                      {L.homeTypes.map((h) => (
                        <Chip key={h} testid={`wiz-hometype-${h}`} active={form.homeType === h} onClick={() => set("homeType", h)}>{h}</Chip>
                      ))}
                    </div>
                  </Field>
                </>
              )}

              {step === 2 && (
                <>
                  <Field optionalText={L.optional} label={L.frequency}>
                    <div className="flex flex-wrap gap-2">
                      {L.frequencies.map((fr) => (
                        <Chip key={fr} testid={`wiz-freq-${fr}`} active={form.frequency === fr} onClick={() => set("frequency", fr)}>{fr}</Chip>
                      ))}
                    </div>
                  </Field>
                  <Field optionalText={L.optional} label={L.startDate}>
                    <Input data-testid="wiz-startdate" type="date" value={form.startDate} onChange={setInput("startDate")} className={fieldCls} />
                  </Field>
                  <Field optionalText={L.optional} label={L.hours} optional>
                    <div className="flex flex-wrap gap-2">
                      {L.hoursOpts.map((h) => (
                        <Chip key={h} testid={`wiz-hours-${h}`} active={form.hours === h} onClick={() => set("hours", form.hours === h ? "" : h)}>{h}</Chip>
                      ))}
                    </div>
                  </Field>
                </>
              )}

              {step === 3 && (
                <>
                  <p className="rounded-xl bg-blue-50 px-4 py-3 text-sm text-blue-700">{L.optionalHint}</p>
                  <div className="grid grid-cols-2 gap-4">
                    <Field optionalText={L.optional} label={L.bedrooms} optional>
                      <select data-testid="wiz-bedrooms" value={form.bedrooms} onChange={setInput("bedrooms")} className="h-10 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm focus:border-blue-600 focus:outline-none focus:ring-1 focus:ring-blue-600">
                        <option value="">—</option>
                        {["1","2","3","4","5","6","7","8","9","10+"].map((n) => <option key={n} value={n}>{n}</option>)}
                      </select>
                    </Field>
                    <Field optionalText={L.optional} label={L.bathrooms} optional>
                      <select data-testid="wiz-bathrooms" value={form.bathrooms} onChange={setInput("bathrooms")} className="h-10 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm focus:border-blue-600 focus:outline-none focus:ring-1 focus:ring-blue-600">
                        <option value="">—</option>
                        {["1","2","3","4","5+"].map((n) => <option key={n} value={n}>{n}</option>)}
                      </select>
                    </Field>
                  </div>
                  <Field optionalText={L.optional} label={L.days} optional>
                    <div className="flex flex-wrap gap-2">
                      {L.daysOpts.map((d) => (
                        <Chip key={d} testid={`wiz-day-${d}`} active={form.preferredDays.includes(d)} onClick={() => toggleArr("preferredDays", d)}>{d}</Chip>
                      ))}
                    </div>
                  </Field>
                  <div className="grid gap-4 sm:grid-cols-2">
                    <Field optionalText={L.optional} label={L.pets} optional>
                      <div className="flex flex-wrap gap-2">
                        {L.petsOpts.map((p) => <Chip key={p} testid={`wiz-pets-${p}`} active={form.pets === p} onClick={() => set("pets", form.pets === p ? "" : p)}>{p}</Chip>)}
                      </div>
                    </Field>
                    <Field optionalText={L.optional} label={L.fixed} optional>
                      <div className="flex flex-wrap gap-2">
                        {L.fixedOpts.map((p) => <Chip key={p} testid={`wiz-fixed-${p}`} active={form.fixedCleaner === p} onClick={() => set("fixedCleaner", form.fixedCleaner === p ? "" : p)}>{p}</Chip>)}
                      </div>
                    </Field>
                  </div>
                  <Field optionalText={L.optional} label={L.notes} optional>
                    <Textarea data-testid="wiz-notes" rows={3} value={form.notes} onChange={setInput("notes")} placeholder={L.notesPh} className={fieldCls} />
                  </Field>
                </>
              )}

              {step === 4 && (
                <>
                  <h3 className="font-heading text-lg font-semibold text-slate-900">{L.reviewTitle}</h3>
                  <div data-testid="wiz-summary" className="rounded-2xl bg-slate-50 p-4">
                    <SummaryRow label={L.name} value={form.name} />
                    <SummaryRow label={L.email} value={form.email} />
                    <SummaryRow label={L.phone} value={form.phone} />
                    <SummaryRow label={L.street} value={form.street} />
                    <SummaryRow label={L.postcode} value={form.postcode} />
                    <SummaryRow label={L.city} value={form.city} />
                    <SummaryRow label={L.homeType} value={form.homeType} />
                    <SummaryRow label={L.frequency} value={form.frequency} />
                    <SummaryRow label={L.startDate} value={form.startDate} />
                    <SummaryRow label={L.hours} value={form.hours} />
                    <SummaryRow label={L.bedrooms} value={form.bedrooms} />
                    <SummaryRow label={L.bathrooms} value={form.bathrooms} />
                    <SummaryRow label={L.days} value={form.preferredDays.join(", ")} />
                    <SummaryRow label={L.pets} value={form.pets} />
                    <SummaryRow label={L.fixed} value={form.fixedCleaner} />
                    <SummaryRow label={L.notes} value={form.notes} />
                  </div>
                </>
              )}
            </motion.div>
          </AnimatePresence>

          {error && <p data-testid="wiz-error" className="mt-4 text-sm font-medium text-red-500">{error}</p>}
        </div>

        <div className="flex items-center justify-between gap-3 border-t border-slate-100 px-6 py-4">
          <Button data-testid="wiz-prev-btn" variant="outline" onClick={prev} disabled={step === 0} className="rounded-full">
            <ArrowLeft className="mr-1 h-4 w-4" /> {L.prev}
          </Button>
          <div className="flex items-center gap-2">
            {isOptionalStep && (
              <Button data-testid="wiz-skip-btn" variant="ghost" onClick={next} className="rounded-full text-slate-500 hover:text-slate-800">
                {L.skip}
              </Button>
            )}
            {step < TOTAL - 1 ? (
              <Button data-testid="wiz-next-btn" onClick={next} className="rounded-full bg-blue-600 px-6 font-semibold text-white hover:bg-blue-700">
                {L.next} <ArrowRight className="ml-1 h-4 w-4" />
              </Button>
            ) : (
              <Button data-testid="wiz-submit-btn" onClick={submit} disabled={loading} className="rounded-full bg-blue-600 px-6 font-semibold text-white hover:bg-blue-700">
                {loading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />{L.sending}</> : <><Send className="mr-2 h-4 w-4" />{L.submit}</>}
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
